import {
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import { Container } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";

import DateInput from "@/components/shared/forms/DateInput";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import RadioFieldRow from "@/components/shared/forms/RadioFieldRow";
import RadioSelect from "@/components/shared/forms/RadioSelect";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { STFF, STFF_TEMPLATE } from "@/models/stff";
import { useCoreData } from "@/queries.ts";

import { FiUser, FiUsers } from "react-icons/fi";
import { LuBuilding2 } from "react-icons/lu";
import { useSTFFFormLogic } from "./hooks/useSTFFFormLogic";
import { LanguageMultiSelect } from "@/components/shared/LanguageMultiSelect";
import { validateMinDate } from "@/utils/util";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";

type Option = {
  Text: string;
  Value: string;
};

type MultiSelectFieldProps = {
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  disabled: boolean;
  options: Option[];
  initialValues: string[];
  onChangeValues: (values: string[]) => void;
  maxSelections: number;
  placeholder: string;
  resetKey: string;
  errorMessage?: string;
  afterChange?: () => void;
};

function MultiSelectField({
  id,
  ariaLabel,
  ariaDescribedBy,
  disabled,
  options,
  initialValues,
  onChangeValues,
  maxSelections,
  placeholder,
  resetKey,
  errorMessage,
  afterChange,
}: MultiSelectFieldProps) {
  const [selected, setSelected] = useState<Option[]>(() => {
    const initialSet = initialValues.filter(Boolean);
    return initialSet
      .map((val) => options.find((o) => o.Value === val))
      .filter((o): o is Option => !!o)
      .slice(0, maxSelections);
  });

  useEffect(() => {
    const initialSet = initialValues.filter(Boolean);
    const mapped = initialSet
      .map((val) => options.find((o) => o.Value === val))
      .filter((o): o is Option => !!o)
      .slice(0, maxSelections);

    setSelected(mapped);
  }, [resetKey, initialValues, options, maxSelections]);

  const handleChange = (e: MultiSelectChangeEvent) => {
    const next = (e.value as Option[]) ?? [];
    const limited = next.slice(0, maxSelections);

    setSelected(limited);
    onChangeValues(limited.map((o) => o.Value));
    afterChange?.();
  };

  useMultiSelectChipListLabel(id, ariaLabel, selected);

  // See MultiSelectToggleButton: gives keyboard users a way to browse the
  // option list without having to type first.
  const [opened, setOpened] = useState(false);

  useMultiSelectListboxMultiselectable(id, opened);

  return (
    <MultiSelect
      id={id}
      ariaLabel={ariaLabel}
      ariaDescribedBy={ariaDescribedBy}
      data={options}
      textField="Text"
      dataItemKey="Value"
      value={selected}
      onChange={handleChange}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder={placeholder}
      valid={!errorMessage}
      validationMessage={errorMessage}
      opened={opened}
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      itemRender={multiSelectSelectedItemRender}
      popupSettings={id ? { className: `${id}-listbox-popup` } : undefined}
      tagRender={multiSelectTagRender}
      suffix={
        <>
          <MultiSelectToggleButton
            opened={opened}
            onToggle={() => setOpened((prev) => !prev)}
            disabled={disabled}
          />
          {selected.length > 0 && (
            <MultiSelectClearButton
              onClear={() =>
                handleChange({ value: [] } as MultiSelectChangeEvent)
              }
              disabled={disabled}
            />
          )}
        </>
      }
    />
  );
}

export function STFFAddEditForm({
  selectedStaff,
  form,
  staffDefaults,
}: {
  selectedStaff: STFF | undefined;
  form: UseFormReturn<STFF, any, STFF>;
  staffDefaults?: Partial<STFF>;
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/staff/$staffId",
  });

  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("STFFEdit");
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("STFF");

  if (!coreData) {
    return <div>Loading...</div>;
  }

  const newStaffTemplate: STFF = {
    ...STFF_TEMPLATE,
    LocalStaffId: params?.staffId ?? "",
    ReportingLEA: leaContext.currentUserOrg.cdsCode || "",
    ...(staffDefaults || {}),
  };

  const staff = selectedStaff ?? newStaffTemplate;
  const isNewRecord = !selectedStaff;

  const {
    control,
    register,
    formState: { errors },
    watch,
  } = form;

  // Use the custom hook for all form logic
  const {
    getRaces,
    setRaces,
    getLanguages,
    setLanguages,
    validateWaiverField,
    validateWaiverIssuedDate,
    validateWaiverExpirationDate,
    validatePermitField,
    validatePermitIssuedDate,
    validatePermitExpirationDate,
    validatePermitNumber,
    validatePermitType,
    validateCurrentEmploymentStartDate,
    validateOverallEmploymentStartDate,
    validateEffectiveStartDate,
    trigger,
  } = useSTFFFormLogic(form, staff);

  const resetKey =
    selectedStaff?.LocalStaffId || `new:${params?.staffId ?? ""}`;
  const staffWaiver = watch("StaffWaiver");
  const programYear = watch("ProgramYear");
  const effectiveStartDate = watch("EffectiveStartDate");
  const waiverIssuedDate = watch("StaffWaiverIssuedDate");
  const waiverExpirationDate = watch("StaffWaiverExpirationDate");
  const permitIssuedDate = watch("StaffPermitIssuedDate");
  const permitExpirationDate = watch("StaffPermitExpirationDate");
  const currentEmploymentStartDate = watch("StaffCurrentEmploymentStartDate");
  const overallEmploymentStartDate = watch("StaffOverallEmploymentStartDate");
  const permitNumber = watch("StaffPermitOrCredentialNumber");
  const isWaiverFieldsDisabled = viewOnly || staffWaiver === "N";

  const hasPermitNumber =
    permitNumber != null && String(permitNumber).trim() !== "";

  const languageOptions = (coreData.languagesList ?? []).map((x: any) => ({
    Text: x.Text ?? x.text ?? "",
    Value: x.Value ?? x.value ?? "",
  }));

  const selectedLanguages = getLanguages()
    .filter(Boolean)
    .map((val) => languageOptions.find((o: Option) => o.Value === val))
    .filter((o): o is Option => !!o);

  const handleLanguagesChange = (items: Option[]) => {
    const limited = items.slice(0, 5);
    setLanguages(limited.map((o) => o.Value));
    trigger("StaffLanguage1");
  };

  // Validate Effective Start Date when Program Year changes
  useEffect(() => {
    if (effectiveStartDate && programYear) {
      trigger("EffectiveStartDate");
    }
  }, [programYear, effectiveStartDate, trigger]);

  // Validate Waiver Expiration Date when Waiver Issued Date changes
  useEffect(() => {
    if (waiverIssuedDate && waiverExpirationDate) {
      trigger("StaffWaiverExpirationDate");
    }
  }, [waiverIssuedDate, waiverExpirationDate, trigger]);

  // Validate Waiver Issued Date when Effective Start Date changes (for the "within 1 year" rule)
  useEffect(() => {
    if (waiverIssuedDate && effectiveStartDate) {
      trigger("StaffWaiverIssuedDate");
    }
  }, [effectiveStartDate, waiverIssuedDate, trigger]);

  const hasPermitIssuedDateError = useRef(false);
  hasPermitIssuedDateError.current = !!errors.StaffPermitIssuedDate;

  useEffect(() => {
    if (hasPermitIssuedDateError.current) {
      trigger("StaffPermitIssuedDate");
    }
  }, [permitIssuedDate, trigger]);

  // Validate Permit Expiration Date when Permit Issued Date changes
  useEffect(() => {
    if (permitIssuedDate && permitExpirationDate) {
      trigger("StaffPermitExpirationDate");
    }
  }, [permitIssuedDate, permitExpirationDate, trigger]);

  // Validate Permit Issued Date when Effective Start Date or Date of Birth changes
  useEffect(() => {
    if (permitIssuedDate && effectiveStartDate) {
      trigger("StaffPermitIssuedDate");
    }
  }, [effectiveStartDate, permitIssuedDate, trigger]);

  // Validate Current Employment Start Date when Effective Start Date changes
  useEffect(() => {
    if (currentEmploymentStartDate && effectiveStartDate) {
      trigger("StaffCurrentEmploymentStartDate");
    }
  }, [effectiveStartDate, currentEmploymentStartDate, trigger]);

  // Validate Overall Employment Start Date when Current Employment Start Date changes
  useEffect(() => {
    if (overallEmploymentStartDate && currentEmploymentStartDate) {
      trigger("StaffOverallEmploymentStartDate");
    }
  }, [currentEmploymentStartDate, overallEmploymentStartDate, trigger]);

  return (
    <Container
      className="ps-3 pe-3 personal-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FiUsers className="section-icon" />
          <h5 className="section-title">Staff Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="SEID"
              labelText="SEID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!staff.SEID}
                  maxLength={10}
                  {...register(id, {
                    validate: (value) => {
                      if (!value || value.trim() === "") return true;
                      if (value.length !== 10) {
                        return "SEID must be exactly 10 characters";
                      }
                      return true;
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="LocalStaffId"
              labelText="Staff Local ID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!staff.LocalStaffId}
                  maxLength={50}
                  {...register(id, { required: "Staff Local ID is required" })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedStaff?.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(id, { required: "Program Year is required" })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    required: "Effective start date is required",
                    validate: validateEffectiveStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FiUser className="section-icon" />
          <h5 className="section-title">Personal Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffLegalFirstName"
              labelText="First Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(id, { required: "First name is required" })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffLegalMiddleName"
              labelText="Middle Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(id)}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffLegalLastName"
              labelText="Last Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={50}
                  {...register(id, { required: "Last name is required" })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffDateOfBirth"
              labelText="Date of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    required: "Date of birth is required",
                    validate: validateMinDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffGender"
              labelText="Gender"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Gender is required" })}
                  options={coreData.genderList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <RadioFieldRow
              name="StaffEthnicity"
              labelText="Hispanic"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <RadioSelect
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Hispanic field is required" })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                    { Text: "No Response", Value: "" },
                  ]}
                />
              )}
            </RadioFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffRace1"
              labelText="Race(s)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <div>
                  <input type="hidden" {...register("StaffRace1")} />
                  <input type="hidden" {...register("StaffRace2")} />
                  <input type="hidden" {...register("StaffRace3")} />
                  <input type="hidden" {...register("StaffRace4")} />
                  <input type="hidden" {...register("StaffRace5")} />
                  <input
                    type="hidden"
                    {...register("StaffEthnicityMissingIndicator")}
                  />
                  <input
                    type="hidden"
                    {...register("StaffRaceMissingIndicator")}
                  />
                  <MultiSelectField
                    id={id}
                    ariaLabel="Race(s)"
                    ariaDescribedBy={`${id}-description`}
                    key={`${resetKey}:races`}
                    disabled={viewOnly}
                    resetKey={resetKey}
                    options={coreData.raceList}
                    initialValues={getRaces()}
                    maxSelections={5}
                    placeholder="Select up to five races"
                    onChangeValues={setRaces}
                    afterChange={() => trigger("StaffRace1")}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffLanguage1"
              labelText="Language(s) Proficient"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name, validationProps) => (
                <div>
                  <input
                    type="hidden"
                    {...register("StaffLanguage1", {
                      required: "At least one language is required",
                    })}
                  />
                  <input type="hidden" {...register("StaffLanguage2")} />
                  <input type="hidden" {...register("StaffLanguage3")} />
                  <input type="hidden" {...register("StaffLanguage4")} />
                  <input type="hidden" {...register("StaffLanguage5")} />
                  <LanguageMultiSelect
                    id={id}
                    ariaLabel="Language(s) Proficient"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={viewOnly}
                    required
                    invalid={!!validationProps["aria-invalid"]}
                    data={languageOptions}
                    value={selectedLanguages}
                    onChange={handleLanguagesChange}
                    placeholder="Select up to five languages"
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffHighestDegreeCode"
              labelText="Highest Education Level"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, {
                    required: "Staff highest degree is required",
                  })}
                  options={coreData.educationList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <LuBuilding2 className="section-icon" />
          <h5 className="section-title">Employment Information</h5>
        </div>

        {/* Row 1: Waiver, Waiver Issued Date, Waiver Expiration Date */}
        <div className="row g-3">
          <div className="col-md-4">
            <RadioFieldRow
              name="StaffWaiver"
              labelText="Waiver?"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <RadioSelect
                  id={id}
                  disabled={viewOnly}
                  {...register(id)}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </RadioFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffWaiverIssuedDate"
              labelText="Waiver Issued Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={staffWaiver === "Y"}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={isWaiverFieldsDisabled}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validateWaiverIssuedDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffWaiverExpirationDate"
              labelText="Waiver Expiration Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={staffWaiver === "Y"}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={isWaiverFieldsDisabled}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validateWaiverExpirationDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="StaffWaiverType"
              labelText="Waiver Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={staffWaiver === "Y"}
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={isWaiverFieldsDisabled}
                  {...register(id, {
                    validate: (value) =>
                      validateWaiverField(value, "Waiver Type"),
                  })}
                  options={coreData.waiverTypes}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="StaffPermitOrCredentialType"
              labelText="Permit/Credential Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={hasPermitNumber}
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, {
                    validate: validatePermitType,
                  })}
                  options={coreData.staffPermitList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffPermitOrCredentialNumber"
              labelText="Permit/Credential Number"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={15}
                  {...register(id, {
                    validate: validatePermitNumber,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffPermitIssuedDate"
              labelText="Permit/Credential Issued Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={hasPermitNumber}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validatePermitIssuedDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffPermitExpirationDate"
              labelText="Permit/Credential Expiration Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={hasPermitNumber}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validatePermitExpirationDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="StaffCurrentEmploymentStartDate"
              labelText="Current Employment Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validateCurrentEmploymentStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffCurrentEmploymentEndDate"
              labelText="Current Employment End Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, { validate: validateMinDate })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="StaffOverallEmploymentStartDate"
              labelText="Overall Employment Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validateOverallEmploymentStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <span
          id="spnAddOrEditStaffErrorOverall"
          className="row ps-3 mt-3 pe-3 float-end"
          style={{ color: "#CC0000" }}
        ></span>
      </div>
    </Container>
  );
}
